import { DateTime } from 'luxon';

/**
 * Small Luxon-backed date helpers, replacing the handful of `moment` operations
 * previously used across event create/edit/settings components. Kept tiny and
 * explicit so each call site maps 1:1 to what it did before.
 *
 * Inputs are whatever the datetime-picker / forms hand us — a native `Date`
 * (the picker now emits Date), a millis number, an ISO string, or null.
 */

type DateLike = Date | string | number | null | undefined;

/** Coerce any picker/form value into a Luxon DateTime (local zone). */
function toDT(value: DateLike): DateTime {
  if (value == null) {
    return DateTime.invalid('null input');
  }
  if (value instanceof Date) {
    return DateTime.fromJSDate(value);
  }
  if (typeof value === 'number') {
    return DateTime.fromMillis(value);
  }
  // string
  const iso = DateTime.fromISO(value);
  return iso.isValid ? iso : DateTime.fromJSDate(new Date(value));
}

/** `moment(x).toDate()` — normalize a picker/form value to a native Date. */
export function toDate(value: DateLike): Date {
  return toDT(value).toJSDate();
}

/** `moment()` current time as a native Date. */
export function nowDate(): Date {
  return new Date();
}

/** `moment.unix(seconds).toDate()` — convert an epoch-seconds timestamp. */
export function unixToDate(seconds: number): Date {
  return DateTime.fromSeconds(seconds).toJSDate();
}

/** `moment(start).add(n, unit).toDate()` — add minutes to a date. */
export function addMinutes(value: DateLike, minutes: number): Date {
  return toDT(value).plus({ minutes }).toJSDate();
}

/** `moment().format('YYYY-MM-DDTHH:mm')` — string for datetime-local inputs. */
export function toDatetimeLocalString(value?: DateLike): string {
  const dt = value == null ? DateTime.now() : toDT(value);
  return dt.toFormat("yyyy-MM-dd'T'HH:mm");
}

/**
 * Duration between two dates, broken into days/hours/minutes.
 * Replaces `moment.duration(moment(end).diff(moment(start)))` + `.asDays()`,
 * `.hours()`, `.minutes()`.
 */
export function durationParts(
  start: DateLike,
  end: DateLike
): { totalDays: number; days: number; hours: number; minutes: number; totalMillis: number } {
  const s = toDT(start);
  const e = toDT(end);
  const diff = e.diff(s, ['days', 'hours', 'minutes']);
  const totalMillis = e.toMillis() - s.toMillis();
  return {
    totalDays: totalMillis / (1000 * 60 * 60 * 24),
    days: Math.floor(diff.days),
    hours: Math.floor(diff.hours),
    minutes: Math.floor(diff.minutes),
    totalMillis,
  };
}

/** `end.isSameOrBefore(start)` — true when end <= start. */
export function isSameOrBefore(end: DateLike, start: DateLike): boolean {
  return toDT(end).toMillis() <= toDT(start).toMillis();
}
